import { Camera } from "../render/Camera";
import { Loop } from "../sim/Loop";
import type { BenchScene } from "./scenes";
import type { EngineDef } from "./engines";
import { drawRunner, type EngineRunner } from "./runner";

const DT = 1 / 60;

export interface LiveStat {
	id: string;
	name: string;
	hue: number;
	primary: boolean;
	msPerStep: number;
	bodies: number;
}

export interface EngineResult {
	id: string;
	name: string;
	hue: number;
	primary: boolean;
	bodies: number;
	/** Temporal substeps per step at this engine's defaults. */
	substeps: number;
	/** Approx total solver sweeps per step (substeps × iterations/substep). */
	solverSweeps: number;
	/** True when `solverSweeps` is an approximation of the engine's internals. */
	approxSweeps: boolean;
	msPerStep: number;
	stepsPerSec: number;
}

export interface CompareResult {
	scene: string;
	steps: number;
	samples: number;
	/** Sorted fastest-first (highest steps/sec). */
	results: EngineResult[];
}

interface Lane {
	def: EngineDef;
	canvas: HTMLCanvasElement;
	ctx: CanvasRenderingContext2D;
	runner: EngineRunner;
	ema: number;
	cssW: number;
	cssH: number;
}

/**
 * Runs one benchmark scene across the whole field of engines side by side: a
 * live animation for visual proof, plus an on-demand measured comparison that
 * returns a fastest-first leaderboard.
 */
export class Arena {
	private readonly camera = new Camera(0, 0, 16);
	private readonly loop: Loop;
	private readonly lanes: Lane[] = [];

	private scene: BenchScene | null = null;
	private dpr = 1;
	private accumulator = 0;
	private paused = false;
	private visible = true;
	private measuring = false;

	constructor(
		defs: { def: EngineDef; canvas: HTMLCanvasElement }[],
		private readonly onLive: (stats: LiveStat[]) => void,
	) {
		for (const { def, canvas } of defs) {
			this.lanes.push({
				def,
				canvas,
				ctx: ctx2d(canvas),
				runner: null as unknown as EngineRunner,
				ema: 0,
				cssW: 0,
				cssH: 0,
			});
		}
		this.loop = new Loop(this.frame);

		const ro = new ResizeObserver(() => this.resize());
		if (this.lanes[0]) ro.observe(this.lanes[0].canvas);
		window.addEventListener("orientationchange", () => this.resize());
	}

	load(scene: BenchScene): void {
		this.scene = scene;
		this.camera.centerX = scene.camera.x;
		this.camera.centerY = scene.camera.y;
		this.camera.scale = scene.camera.scale;
		for (const lane of this.lanes) {
			lane.runner = lane.def.create(scene);
			lane.ema = 0;
		}
		this.accumulator = 0;
		this.paused = false;
		this.resize();
		this.updateLoopState();
	}

	reset(): void {
		if (this.scene) this.load(this.scene);
	}

	/**
	 * Start/stop the live loop based on viewport visibility. The arena steps
	 * four engines (one of them p2.js at ~9 ms/step) every frame, so leaving it
	 * running while the visitor is looking at the demo gallery above would starve
	 * the whole main thread. We only animate it while it's actually on screen.
	 */
	setVisible(on: boolean): void {
		this.visible = on;
		this.updateLoopState();
	}

	togglePause(): boolean {
		this.paused = !this.paused;
		return this.paused;
	}

	get isPaused(): boolean {
		return this.paused;
	}

	/**
	 * Measure every engine on *fresh* worlds (independent of the live animation).
	 * Warms up, then times `measured` fixed steps each. Yields between engines so
	 * the UI can report progress. Returns a fastest-first leaderboard.
	 */
	async runComparison(
		onPhase: (label: string) => void,
		warmup = 180,
		measured = 400,
		samples = 4,
	): Promise<CompareResult> {
		const scene = this.scene!;
		return this.withMeasurementPaused(() =>
			measureSceneComparison(
				this.lanes.map((lane) => lane.def),
				scene,
				onPhase,
				warmup,
				measured,
				samples,
			),
		);
	}

	/**
	 * Measure every engine across *all* scenes and aggregate by geometric mean of
	 * ms/step — the balanced, scene-independent verdict. Geomean (not arithmetic
	 * mean) is the honest aggregate: it isn't dominated by the single slowest
	 * scene and rewards being consistently quick everywhere.
	 */
	async runOverall(
		scenes: BenchScene[],
		onPhase: (label: string) => void,
		warmup = 180,
		measured = 400,
		samples = 4,
	): Promise<CompareResult> {
		return this.withMeasurementPaused(() =>
			measureOverallComparison(
				this.lanes.map((lane) => lane.def),
				scenes,
				onPhase,
				warmup,
				measured,
				samples,
			),
		);
	}

	private async withMeasurementPaused<T>(work: () => Promise<T>): Promise<T> {
		this.measuring = true;
		this.loop.stop();
		this.accumulator = 0;
		try {
			await settleBrowser();
			return await work();
		} finally {
			this.measuring = false;
			await settleBrowser();
			this.updateLoopState();
		}
	}

	private updateLoopState(): void {
		if (this.scene && this.visible && !this.measuring) this.loop.start();
		else this.loop.stop();
	}

	private resize(): void {
		this.dpr = Math.min(window.devicePixelRatio || 1, 2);
		for (const lane of this.lanes) {
			const rect = lane.canvas.getBoundingClientRect();
			lane.cssW = rect.width;
			lane.cssH = rect.height;
			lane.canvas.width = Math.max(1, Math.round(rect.width * this.dpr));
			lane.canvas.height = Math.max(1, Math.round(rect.height * this.dpr));
		}
	}

	private readonly frame = (dt: number): void => {
		if (!this.scene) return;

		const stepMs = this.lanes.map(() => 0);
		let n = 0;
		if (!this.paused) {
			this.accumulator += dt;
			let steps = 0;
			while (this.accumulator >= DT && steps < 8) {
				for (let i = 0; i < this.lanes.length; i++) {
					const a = performance.now();
					this.lanes[i]!.runner.step(DT);
					stepMs[i]! += performance.now() - a;
				}
				this.accumulator -= DT;
				steps++;
				n++;
			}
			if (steps === 8 && this.accumulator > DT) this.accumulator = 0;
		}

		const stats: LiveStat[] = [];
		for (let i = 0; i < this.lanes.length; i++) {
			const lane = this.lanes[i]!;
			lane.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
			this.camera.setViewport(lane.cssW, lane.cssH);
			drawRunner(lane.ctx, this.camera, lane.runner, lane.def.hue);
			if (n > 0) lane.ema += (stepMs[i]! / n - lane.ema) * 0.2;
			stats.push({
				id: lane.def.id,
				name: lane.def.name,
				hue: lane.def.hue,
				primary: !!lane.def.primary,
				msPerStep: lane.ema,
				bodies: lane.runner.bodyCount,
			});
		}
		this.onLive(stats);
	};
}

export async function measureSceneComparison(
	defs: EngineDef[],
	scene: BenchScene,
	onPhase: (label: string) => void,
	warmup = 180,
	measured = 400,
	samples = 4,
): Promise<CompareResult> {
	const byEngine = new Map<string, number[]>();
	const bodies = new Map<string, number>();
	for (const def of defs) byEngine.set(def.id, []);

	for (let sample = 0; sample < samples; sample++) {
		for (const def of rotated(defs, sample)) {
			onPhase(`Measuring ${def.name} · sample ${sample + 1}/${samples}…`);
			const result = await measureFreshRunner(def, scene, warmup, measured);
			byEngine.get(def.id)!.push(result.msPerStep);
			bodies.set(def.id, result.bodies);
		}
	}

	return makeResult(defs, scene.title, measured, samples, byEngine, bodies);
}

export async function measureOverallComparison(
	defs: EngineDef[],
	scenes: BenchScene[],
	onPhase: (label: string) => void,
	warmup = 180,
	measured = 400,
	samples = 4,
): Promise<CompareResult> {
	const logSum = new Map<string, number>();
	const bodies = new Map<string, number>();

	for (let sceneIndex = 0; sceneIndex < scenes.length; sceneIndex++) {
		const scene = scenes[sceneIndex]!;
		const byEngine = new Map<string, number[]>();
		const sceneBodies = new Map<string, number>();
		for (const def of defs) byEngine.set(def.id, []);

		for (let sample = 0; sample < samples; sample++) {
			for (const def of rotated(defs, sample + sceneIndex)) {
				onPhase(
					`Measuring ${def.name} · ${scene.title} · sample ${sample + 1}/${samples}…`,
				);
				const result = await measureFreshRunner(def, scene, warmup, measured);
				byEngine.get(def.id)!.push(result.msPerStep);
				sceneBodies.set(def.id, result.bodies);
			}
		}

		for (const def of defs) {
			const sceneMedian = median(byEngine.get(def.id)!);
			logSum.set(def.id, (logSum.get(def.id) ?? 0) + Math.log(sceneMedian));
			bodies.set(def.id, (bodies.get(def.id) ?? 0) + (sceneBodies.get(def.id) ?? 0));
		}
	}

	const byEngine = new Map<string, number[]>();
	for (const def of defs) {
		const geomean = Math.exp((logSum.get(def.id) ?? 0) / scenes.length);
		byEngine.set(def.id, [geomean]);
		bodies.set(def.id, Math.round((bodies.get(def.id) ?? 0) / scenes.length));
	}

	return makeResult(
		defs,
		`Overall · geomean of ${scenes.length} scenes`,
		measured,
		samples,
		byEngine,
		bodies,
	);
}

function ctx2d(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
	const ctx = canvas.getContext("2d");
	if (!ctx) throw new Error("Canvas 2D context is not available");
	return ctx;
}

function makeResult(
	defs: EngineDef[],
	scene: string,
	steps: number,
	samples: number,
	byEngine: Map<string, number[]>,
	bodies: Map<string, number>,
): CompareResult {
	const results = defs.map((def) => {
		const ms = median(byEngine.get(def.id)!);
		return {
			id: def.id,
			name: def.name,
			hue: def.hue,
			primary: !!def.primary,
			bodies: bodies.get(def.id) ?? 0,
			substeps: def.substeps,
			solverSweeps: def.substeps * def.iterationsPerSubstep,
			approxSweeps: !!def.approxIterations,
			msPerStep: ms,
			stepsPerSec: 1000 / ms,
		};
	});

	results.sort((a, b) => b.stepsPerSec - a.stepsPerSec);
	return { scene, steps, samples, results };
}

async function measureFreshRunner(
	def: EngineDef,
	scene: BenchScene,
	warmup: number,
	measured: number,
): Promise<{ msPerStep: number; bodies: number }> {
	await settleBrowser();
	let runner: EngineRunner | null = def.create(scene);
	try {
		for (let i = 0; i < warmup; i++) runner.step(DT);
		await settleBrowser();
		const bodies = runner.bodyCount;
		const t0 = performance.now();
		for (let i = 0; i < measured; i++) runner.step(DT);
		return { msPerStep: (performance.now() - t0) / measured, bodies };
	} finally {
		runner.dispose?.();
		runner = null;
		await settleBrowser();
	}
}

function rotated<T>(items: readonly T[], offset: number): T[] {
	const out: T[] = [];
	for (let i = 0; i < items.length; i++) out.push(items[(i + offset) % items.length]!);
	return out;
}

function median(values: number[]): number {
	const sorted = [...values].sort((a, b) => a - b);
	const mid = sorted.length >> 1;
	return sorted.length % 2 === 1 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2;
}

async function settleBrowser(): Promise<void> {
	await macrotask();
	await raf();
	await idle();
	const gc = (globalThis as { gc?: () => void }).gc;
	if (typeof gc === "function") gc();
	await macrotask();
}

function raf(): Promise<void> {
	return new Promise((resolve) => requestAnimationFrame(() => resolve()));
}

function macrotask(): Promise<void> {
	return new Promise((resolve) => window.setTimeout(resolve, 0));
}

function idle(): Promise<void> {
	return new Promise((resolve) => {
		if ("requestIdleCallback" in window) {
			window.requestIdleCallback(() => resolve(), { timeout: 80 });
		} else {
			globalThis.setTimeout(resolve, 0);
		}
	});
}
